// RouteUtils.js
// Helpers to parse and build canonical /home routes with optional del_id and open state

const VALID_TABS = new Set(["drafts", "submitted", "approvals", "reporting"]);

export function normalizeTab(tab) {
  if (tab === "submits") return "submitted";
  return VALID_TABS.has(tab) ? tab : null;
}

export function parseHomeRoute(pathname, search) {
  if (!pathname || !pathname.startsWith("/home")) return {};
  const parts = pathname.split("/").filter(Boolean); // ["home", ...]
  const seg = parts[1] || null;
  const tab = normalizeTab(seg || null);

  let delId = null;
  if (parts.length >= 3) {
    const suffix = parts.slice(2).join("/");
    if (suffix.startsWith("del_id=:")) delId = suffix.slice("del_id=:".length);
    else if (suffix.startsWith("del_id="))
      delId = suffix.slice("del_id=".length);
  }

  const params = new URLSearchParams(search || "");
  if (!delId && params.has("del_id")) {
    delId = params.get("del_id") || null;
  }
  let open = false;
  let action = null; // e.g., "approve" | "view" | "edit" | "new"
  if (params.has("open")) {
    const v = (params.get("open") || "").toLowerCase();
    open = v === "1" || v === "true" || v === "yes";
  }
  if (params.has("action")) {
    const a = (params.get("action") || "").toLowerCase();
    if (a === "approve" || a === "view" || a === "edit" || a === "new")
      action = a;
  }

  return { tab, delId, action, open };
}

export function buildCanonicalHomePath({ tab, delId, action, open }) {
  const normTab = normalizeTab(tab);
  const base = normTab ? `/home/${normTab}` : "/home";
  const path = delId ? `${base}/del_id=${delId}` : base;
  const query = new URLSearchParams();
  if (action) query.set("action", action);
  if (open) query.set("open", "1");
  const qs = query.toString();
  return qs ? `${path}?${qs}` : `${path}`;
}

export function canonicalizeHomeLocation(locationLike) {
  const pathname = locationLike?.pathname || "";
  const search = locationLike?.search || "";
  if (!pathname.startsWith("/home") || pathname === "/home") return null;
  const parsed = parseHomeRoute(pathname, search);
  // Defaults: if delId is present but action/open missing -> default per tab
  if (parsed.delId) {
    if (!parsed.action)
      parsed.action = parsed.tab === "drafts" ? "edit" : "view";
    if (!parsed.open) parsed.open = true;
  } else if (parsed.tab === "drafts" && parsed.action === "new") {
    // new drafts always open
    parsed.open = true;
  }
  if (!parsed.tab) return null;
  const canonical = buildCanonicalHomePath(parsed);
  const current = `${pathname}${search || ""}`;
  return canonical !== current ? canonical : null;
}

// Convenience helper for tabs to extract delId for a specific tab from the current location
export function getDeepLinkedDelId(locationLike, expectedTab) {
  const pathname = locationLike?.pathname || "";
  const search = locationLike?.search || "";
  const { tab, delId } = parseHomeRoute(pathname, search);
  if (tab && expectedTab && normalizeTab(expectedTab) === tab) {
    return delId || null;
  }
  return null;
}
